/**
 * Dev tools — destructive shortcuts behind a confirmation prompt.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Bomb, Loader2, Volume2 } from "lucide-react";
import {
  useClearLastPlayed,
  useClearStats,
  useLastPlayedStorage,
  useNetworkStats,
} from "../../hooks/useStats";
import { useDeleteAllTracks } from "../../hooks/useTracks";
import { devApi, type LoudnessScanJobDto } from "../../lib/api";
import { LineGraph } from "../../components/LineGraph";

export const DevTools: React.FC = () => {
  const [busy, setBusy] = useState<string | null>(null);
  const [loudnessScanning, setLoudnessScanning] = useState(false);
  const [skipProcessedLoudness, setSkipProcessedLoudness] = useState(true);
  const [loudnessProgress, setLoudnessProgress] = useState<{
    current: number;
    total: number;
    updated: number;
    skipped: number;
    trackTitle: string | null;
  } | null>(null);
  const [loudnessResult, setLoudnessResult] = useState<string | null>(null);
  const [loudnessError, setLoudnessError] = useState<string | null>(null);
  const loudnessEsRef = useRef<EventSource | null>(null);
  const loudnessDoneRef = useRef(false);
  const qc = useQueryClient();

  const deleteAll = useDeleteAllTracks();
  const clearStats = useClearStats();
  const clearLastPlayed = useClearLastPlayed();
  const lastPlayed = useLastPlayedStorage();
  const networkStats = useNetworkStats();

  const applyLoudnessJob = useCallback(
    (job: LoudnessScanJobDto): void => {
      setSkipProcessedLoudness(job.skipProcessed);
      setLoudnessScanning(job.status === "running");
      setLoudnessProgress(job.progress);
      setLoudnessError(job.status === "failed" ? (job.error ?? "Scan failed") : null);

      if (job.status === "completed" && job.result) {
        setLoudnessResult(
          `Done — ${job.result.scanned} in library, ${job.result.updated} updated, ${job.result.skipped} skipped.`,
        );
        loudnessDoneRef.current = true;
        void qc.invalidateQueries({ queryKey: ["tracks"] });
      } else if (job.status === "running") {
        setLoudnessResult(null);
        loudnessDoneRef.current = false;
      }
    },
    [qc],
  );

  const connectLoudnessStream = useCallback((): void => {
    if (loudnessEsRef.current) return;
    const es = new EventSource(devApi.loudnessScanStreamUrl(), {
      withCredentials: true,
    });
    loudnessEsRef.current = es;

    es.addEventListener("snapshot", (ev) => {
      try {
        applyLoudnessJob(
          JSON.parse((ev as MessageEvent).data) as LoudnessScanJobDto,
        );
      } catch {
        setLoudnessError("Invalid loudness scan status from server");
      }
    });

    es.onerror = () => {
      // EventSource reconnects automatically. Keep the latest server-owned
      // progress visible instead of declaring the detached job failed.
      if (es.readyState === EventSource.CLOSED) {
        loudnessEsRef.current = null;
      }
    };
  }, [applyLoudnessJob]);

  useEffect(() => {
    let cancelled = false;
    void devApi
      .loudnessScanStatus()
      .then(({ job }) => {
        if (cancelled) return;
        applyLoudnessJob(job);
        if (job.status === "running") connectLoudnessStream();
      })
      .catch((err) => {
        if (!cancelled) {
          setLoudnessError(
            err instanceof Error ? err.message : "Failed to load scan status",
          );
        }
      });

    return () => {
      cancelled = true;
      loudnessEsRef.current?.close();
      loudnessEsRef.current = null;
    };
  }, [applyLoudnessJob, connectLoudnessStream]);

  const startLoudnessScan = (): void => {
    if (loudnessScanning) return;
    const scope = skipProcessedLoudness
      ? "unprocessed tracks only"
      : "every track (re-scan all)";
    if (!confirm(`Scan loudness for ${scope}? This can take several minutes.`)) return;

    loudnessEsRef.current?.close();
    loudnessEsRef.current = null;
    loudnessDoneRef.current = false;
    setLoudnessScanning(true);
    setLoudnessProgress(null);
    setLoudnessResult(null);
    setLoudnessError(null);

    void (async () => {
      try {
        const { job } = await devApi.scanLoudness(skipProcessedLoudness);
        applyLoudnessJob(job);
        connectLoudnessStream();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Scan failed";
        setLoudnessError(message);
        setLoudnessScanning(false);
      }
    })();
  };

  const run = async (name: string, fn: () => Promise<unknown>): Promise<void> => {
    if (!confirm(`Run "${name}"?`)) return;
    setBusy(name);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <section className="card p-4">
        <h2 className="mb-2 text-sm font-semibold">LastPlayed manipulation</h2>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn"
            disabled={!!busy}
            onClick={() => run("randomize lastPlayed", () => devApi.randomizePlayed())}
          >
            Randomize
          </button>
          <button
            className="btn"
            disabled={!!busy}
            onClick={() => run("clear lastPlayed", () => devApi.clearPlayed())}
          >
            Clear
          </button>
          <button
            className="btn"
            disabled={!!busy}
            onClick={() => run("refresh metadata", () => devApi.refreshMetadata())}
          >
            Refresh metadata
          </button>
          <button
            className="btn"
            disabled={!!busy}
            onClick={() => run("backfill metadata keys", () => devApi.backfillMetadataKeys())}
          >
            Backfill metadata keys
          </button>
          <button
            className="btn"
            disabled={!!busy}
            onClick={() => run("clear lastPlayed storage", () => clearLastPlayed.mutateAsync())}
          >
            Wipe lastPlayed backup
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Backup contains {lastPlayed.data?.count ?? "?"} entries.
        </p>
      </section>

      <section className="card p-4">
        <h2 className="mb-2 text-sm font-semibold">Stats</h2>
        <button
          className="btn btn-danger"
          disabled={!!busy}
          onClick={() => run("clear all stats", () => clearStats.mutateAsync())}
        >
          <Bomb className="h-4 w-4" /> Clear all stats
        </button>
      </section>

      <section className="card p-4">
        <h2 className="mb-2 text-sm font-semibold">Audio / loudness</h2>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Scans library files with ffmpeg loudnorm and stores per-track{" "}
          <code className="text-[11px]">loudnessGainDb</code> for normalization.
        </p>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={skipProcessedLoudness}
            onChange={(e) => setSkipProcessedLoudness(e.target.checked)}
            disabled={loudnessScanning}
          />
          Skip tracks already marked Norm (have loudness data)
        </label>
        <button
          className="btn btn-primary"
          disabled={!!busy || loudnessScanning}
          onClick={startLoudnessScan}
        >
          {loudnessScanning ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Volume2 className="h-4 w-4" />
          )}
          {loudnessScanning
            ? "Scanning…"
            : skipProcessedLoudness
              ? "Scan loudness (unprocessed only)"
              : "Scan loudness (re-scan all)"}
        </button>

        {loudnessScanning || loudnessProgress ? (
          <div
            className="mt-4 space-y-2 rounded-xl border border-indigo-200 bg-indigo-50/80 p-3 dark:border-indigo-900 dark:bg-indigo-950/40"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center justify-between gap-2 text-xs font-medium text-indigo-900 dark:text-indigo-100">
              <span className="inline-flex items-center gap-1.5">
                {loudnessScanning ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : null}
                {loudnessScanning ? "Scanning library…" : "Scan complete"}
              </span>
              {loudnessProgress && loudnessProgress.total > 0 ? (
                <span className="tabular-nums">
                  {loudnessProgress.current} / {loudnessProgress.total}
                </span>
              ) : null}
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-indigo-200/80 dark:bg-indigo-900">
              <div
                className="h-full rounded-full bg-indigo-600 transition-[width] duration-300 ease-out dark:bg-indigo-400"
                style={{
                  width:
                    loudnessProgress && loudnessProgress.total > 0
                      ? `${Math.min(100, (100 * loudnessProgress.current) / loudnessProgress.total)}%`
                      : loudnessScanning
                        ? "8%"
                        : "100%",
                }}
              />
            </div>
            {loudnessProgress?.trackTitle ? (
              <p className="truncate text-xs text-indigo-800/90 dark:text-indigo-200/90">
                {loudnessProgress.trackTitle}
              </p>
            ) : null}
          </div>
        ) : null}

        {loudnessError ? (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{loudnessError}</p>
        ) : null}
        {loudnessResult ? (
          <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">{loudnessResult}</p>
        ) : null}
      </section>

      <section className="card p-4">
        <h2 className="mb-2 text-sm font-semibold">Library</h2>
        <button
          className="btn btn-danger"
          disabled={!!busy}
          onClick={() => run("delete all tracks", () => deleteAll.mutateAsync())}
        >
          <Bomb className="h-4 w-4" /> Delete all tracks
        </button>
      </section>

      {networkStats.data?.connected && (
        <section className="card p-4">
          <h2 className="mb-2 text-sm font-semibold">Voice network stats</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <LineGraph
              data={networkStats.data.history}
              dataKey="ping"
              label="Ping"
              color="#6366f1"
              unit="ms"
            />
            <LineGraph
              data={networkStats.data.history}
              dataKey="bufferHealthPercent"
              label="Buffer health"
              color="#22c55e"
              unit="%"
            />
            <LineGraph
              data={networkStats.data.history}
              dataKey="packetLossPercent"
              label="Packet loss"
              color="#ef4444"
              unit="%"
            />
            <LineGraph
              data={networkStats.data.history}
              dataKey="bitrate"
              label="Bitrate"
              color="#f59e0b"
              unit="kbps"
            />
          </div>
        </section>
      )}
    </div>
  );
};
